/**
 * Creates options for area select input
 * We operate on 2 parameters - area and time;
 * Area is here for purely presentational purposes. We use it to properly format
 * option labels, which should be like `1001 - 1500 sq ft` for each option, increasing by 500
 * for every next option, hence the calculations for `maxArea` and `minArea`.
 * The only exception here is the first option that has a fixed label.
 * Time represents a value assigned to each label, we simply increase it by 15 for every next option
 */
const createAreaOptions = (maxTime) => {
  const areaOptions = [];

  /* eslint-disable-next-line no-plusplus */
  for (let time = -45, area = 500; time <= maxTime; time += 15, area += 500) {
    const minArea = area + 1;
    const maxArea = area + 500;
    const label = area < 1000
      ? 'Under 1000 sq ft'
      : `${minArea} - ${maxArea} sq ft`;
    areaOptions.push({ label, value: time });
  }

  return areaOptions;
};

const createOptions = (config) => {
  const {
    initialValue,
    maxValue,
    step,
    labelSingle = '',
    labelMulti = '',
  } = config;
  const options = [];

  let value = initialValue;
  let n = 1;
  while (value <= maxValue) {
    options.push({
      value,
      label: `${n} ${n > 1 ? labelMulti : labelSingle}`.trim(),
    });

    value += step;
    n += 1;
  }

  return options;
};

const createSimpleFloorOptions = ([carpetValue, hardFloorValue]) => [
  { label: 'Yes - carpet', value: carpetValue },
  { label: 'Yes - hard floor', value: hardFloorValue },
];

const createFloorLevel = (amount, [carpetValue, hardFloorValue]) => [
  { label: `${amount} - carpet`, value: carpetValue },
  { label: `${amount} - hard floor`, value: hardFloorValue },
];

export const defaultSettings = {
  hourly_rate: 30,
  cleaners_amount: 1,
  revised_hourly_rate: 30,
  multipliers: {
    one_time: 1.35,
    first: 1.35,
    weekly: 0.95,
    bi_weekly: 1,
    monthly: 1.15,
    spring_deep: 2,
    move_in_out: 1.75,
  },
  area: createAreaOptions(225),
  spec: {
    stories: createOptions({
      initialValue: 10,
      maxValue: 20,
      step: 5,
    }),
    master_bath: [
      { label: 'Separate shower & tub', value: 45 },
      { label: 'Shower tub combo', value: 35 },
      { label: 'Just shower', value: 40 },
    ],
    full_baths: createOptions({
      initialValue: 30,
      maxValue: 150,
      step: 30,
    }),
    half_bath: createOptions({
      initialValue: 15,
      maxValue: 45,
      step: 15,
    }),
    kitchen: [
      { label: 'Yes', value: 35 },
    ],
    family_room: createSimpleFloorOptions([17.5, 22.5]),
    dining_room: createSimpleFloorOptions([15, 17.5]),
    living_room: createSimpleFloorOptions([15, 17.5]),
    den: createSimpleFloorOptions([15, 17.5]),
    master_bedroom: createSimpleFloorOptions([15, 17.5]),
    bedrooms: [
      ...createFloorLevel(1, [12.5, 15]),
      ...createFloorLevel(2, [25, 30]),
      ...createFloorLevel(3, [37.5, 45]),
      ...createFloorLevel(4, [50, 60]),
      ...createFloorLevel(5, [62.5, 75]),
      ...createFloorLevel(6, [75, 90]),
    ],
    pets: createOptions({
      initialValue: 10,
      maxValue: 25,
      step: 5,
    }),
    bonus_room: createSimpleFloorOptions([15, 17.5]),
    loft: createSimpleFloorOptions([10, 12.5]),
    office: createSimpleFloorOptions([15, 17.5]),
    sunroom: createSimpleFloorOptions([15, 17.5]),
    study: createSimpleFloorOptions([15, 17.5]),
    basement: createSimpleFloorOptions([20, 30]),
    extra: [
      { label: 'Yes', value: 15 },
    ],
    linen_change: createOptions({
      initialValue: 5,
      maxValue: 30,
      step: 5,
      labelSingle: 'bed',
      labelMulti: 'beds',
    }),
    vacuum_furniture: createOptions({
      initialValue: 5,
      maxValue: 30,
      step: 5,
      labelSingle: 'piece',
      labelMulti: 'pieces',
    }),
    inside_fridge: [{ label: 'Yes', value: 30 }],
    inside_oven: [{ label: 'Yes', value: 60 }],
    laundry: [{ label: 'Yes', value: 30 }],
    inside_windows: createOptions({
      initialValue: 5,
      maxValue: 150,
      step: 5,
      labelSingle: 'window',
      labelMulti: 'windows',
    }),
    inside_cabinets: [{ label: 'Yes', value: 60 }],
    wet_wipe_blinds: createOptions({
      initialValue: 15,
      maxValue: 450,
      step: 15,
      labelSingle: 'blind',
      labelMulti: 'blinds',
    }),
  },
};

export const subscriptionActiveStatuses = ['active', 'trialing'];
